import { object, date, number, string, array } from 'yup'
import {
  FieldNames,
  RecurrenceTypes,
  PeriodTypes,
  RecurrenceVariantTypes,
  IntradayRepeatTypes,
} from '../../types/publishSchedule'

export const MaxValues = {
  [FieldNames.everyNthPeriod]: {
    [RecurrenceTypes.none]: 365,
    [RecurrenceTypes.daily]: 365,
    [RecurrenceTypes.weekly]: 52,
    [RecurrenceTypes.monthly]: 12,
    [RecurrenceTypes.yearly]: 1,
  },
  [FieldNames.recurrenceDayNumber]: 31,
  [FieldNames.occurrences]: 999,
}

const validationMessages = {
  [FieldNames.occurrences]: `The end occurrence is required and its value must be between 1 and ${
    MaxValues[FieldNames.occurrences]
  }, inclusive.`,
  [FieldNames.everyNthPeriod]: {
    [RecurrenceTypes.daily]: `The ${
      RecurrenceTypes.daily
    } frequency is required and its value must be between 1 and ${
      MaxValues[FieldNames.everyNthPeriod][RecurrenceTypes.daily]
    }, inclusive.`,
    [RecurrenceTypes.weekly]: `The ${
      RecurrenceTypes.weekly
    } frequency is required and its value must be between 1 and ${
      MaxValues[FieldNames.everyNthPeriod][RecurrenceTypes.weekly]
    }, inclusive.`,
    [RecurrenceTypes.monthly]: `The ${
      RecurrenceTypes.monthly
    } frequency is required and its value must be between 1 and ${
      MaxValues[FieldNames.everyNthPeriod][RecurrenceTypes.monthly]
    }, inclusive.`,
  },
}

export const scheduleValidationSchema = object({
  [FieldNames.startTime]: date()
    .nullable()
    .when(
      ['$currentDate', '$isUpdating'],
      (currentDate: Date, isUpdating: boolean) =>
        isUpdating
          ? date()
              .nullable()
              .required('This field is required.')
          : date()
              .nullable()
              .required('This field is required.')
              .min(currentDate, 'Start time cannot be in the past.')
    ),
  [FieldNames.recurrence]: string(),
  [FieldNames.recurrenceVariant]: string(),
  [FieldNames.recurrenceDayNumber]: number()
    .nullable()
    .when(
      [
        '$daysInSelectedMonth',
        FieldNames.recurrence,
        FieldNames.recurrenceVariant,
      ],
      (
        daysInSelectedMonth: number,
        recurrence: RecurrenceTypes,
        recurrenceVariant: RecurrenceVariantTypes
      ) => {
        const validationMessage =
          recurrence === RecurrenceTypes.monthly
            ? `The day of the month is required and its value must be between 1 and ${
                MaxValues[FieldNames.recurrenceDayNumber]
              }, inclusive.`
            : `The day of the month is required and its value must be between 1 and ${daysInSelectedMonth}, inclusive.`
        return (recurrence === RecurrenceTypes.monthly ||
          recurrence === RecurrenceTypes.yearly) &&
          recurrenceVariant === RecurrenceVariantTypes.dayByNumber
          ? number()
              .nullable()
              .required(validationMessage)
              .min(1, validationMessage)
              .max(
                recurrence === RecurrenceTypes.monthly
                  ? MaxValues[FieldNames.recurrenceDayNumber]
                  : daysInSelectedMonth,
                validationMessage
              )
          : number().nullable()
      }
    ),
  [FieldNames.recurrenceWeekday]: string(),
  [FieldNames.recurrenceWeekdayCount]: number(),
  [FieldNames.recurrenceMonth]: number(),
  [FieldNames.recurrenceWeekdays]: array().of(number()),
  [FieldNames.everyNthPeriod]: number()
    .nullable()
    .when([FieldNames.recurrence], (recurrence: RecurrenceTypes) => {
      const validationMessage =
        validationMessages[FieldNames.everyNthPeriod][recurrence]
      return number()
        .nullable()
        .required(validationMessage)
        .min(1, validationMessage)
        .max(
          MaxValues[FieldNames.everyNthPeriod][recurrence],
          validationMessage
        )
    }),
  [FieldNames.period]: string(),
  [FieldNames.occurrences]: number()
    .nullable()
    .when([FieldNames.period], {
      is: PeriodTypes.untilOccurrence,
      then: () => {
        const validationMessage = validationMessages[FieldNames.occurrences]
        return number()
          .nullable()
          .required(validationMessage)
          .min(1, validationMessage)
          .max(MaxValues[FieldNames.occurrences], validationMessage)
      },
    }),
  [FieldNames.endTime]: date()
    .nullable()
    .when(
      [FieldNames.recurrence, FieldNames.period, '$minEndDate'],
      (recurrence: RecurrenceTypes, period: PeriodTypes, minEndDate: Date) => {
        return recurrence !== RecurrenceTypes.none &&
          period === PeriodTypes.untilDate
          ? date()
              .nullable()
              .required('This field is required.')
              .min(
                minEndDate,
                'The end date and time must come after the start date.'
              )
          : date().nullable()
      }
    ),
  [FieldNames.intradayRepeat]: string(),
  [FieldNames.intradayRepeatInterval]: string(),
  [FieldNames.intradayRepeatUntil]: date()
    .nullable()
    .when(
      [FieldNames.intradayRepeat, FieldNames.startTime, '$currentDate'],
      (
        intradayRepeat: IntradayRepeatTypes,
        startTime: Date | null,
        currentDate: Date
      ) => {
        return intradayRepeat === IntradayRepeatTypes.repeat
          ? date()
              .nullable()
              .required('This field is required.')
              .min(
                startTime || currentDate,
                'The intraday repeat until date and time must come after the start date.'
              )
          : date().nullable()
      }
    ),
})
